
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Ticket, TicketStatus, TicketPriority, PaymentStatus, InternalTicketNote, RepairStageStatus, RepairStage, AppSettings, CommunicationType, CommunicationLogEntry, Employee, EmployeeRole, SLAStatus, RepairChecklist, ChecklistItem, AppointmentModalData, PartUsageEntry, InventoryItem } from '../types';
import { GoogleGenAI } from "@google/genai";
import { PrintableDeviceLabel } from './PrintableDeviceLabel';
import { PrintableCheckInAgreement } from './PrintableCheckInAgreement';
import { PrintableCompletionAgreement } from './PrintableCompletionAgreement';
import { CheckInProcessModal } from './CheckInProcessModal';
import { AISuggestedSolutionsModal } from './AISuggestedSolutionsModal';
import { Button } from './Button';

const getApiKey = () => {
  if (typeof process === 'undefined' || !process.env || !process.env.API_KEY) {
    console.warn("API_KEY not found in process.env. AI features may be disabled or use placeholders.");
    return undefined;
  }
  return process.env.API_KEY;
};

interface TicketItemProps {
  ticket: Ticket;
  appSettings: AppSettings;
  currentUser: Employee | null;
  inventoryItems: InventoryItem[];
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile?: (customerName: string) => void;
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  onShowCustomerPortal?: (ticketId: string) => void;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void;
  onAddCommunicationLog: (ticketId: string, type: CommunicationType, content: string, recipient?: string, staffName?: string) => void;
  onOpenAppointmentModal: (data?: AppointmentModalData) => void;
  onAddPartToTicket: (ticketId: string, inventoryItemId: string, quantityUsed: number, addPriceToTicketTotal: boolean) => void;
  repairChecklists: RepairChecklist[];
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void;
}

// Existing style functions (getStatusClass, getSLAStatusClass, etc.) and Icon components remain the same...
const getStatusClass = (status: TicketStatus): string => {
  switch (status) {
    case TicketStatus.NEW:
      return 'bg-sky-100 text-sky-700 border-sky-300';
    case TicketStatus.IN_PROGRESS:
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case TicketStatus.WAITING_FOR_PARTS:
      return 'bg-purple-100 text-purple-700 border-purple-300';
    case TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL:
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case TicketStatus.READY_FOR_PICKUP:
      return 'bg-[var(--color-accent)] bg-opacity-80 text-black border-[var(--color-accent)]';
    case TicketStatus.COMPLETED:
      return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    case TicketStatus.CUSTOMER_DECLINED:
      return 'bg-red-100 text-red-700 border-red-300';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-300';
  }
};

const getSLAStatusClass = (status?: SLAStatus): string => {
  if (!status) return 'text-slate-500';
  switch (status) {
    case SLAStatus.OK:
    case SLAStatus.COMPLETED_ON_TIME:
      return 'text-emerald-600 font-medium';
    case SLAStatus.AT_RISK:
      return 'text-amber-600 font-medium';
    case SLAStatus.BREACHED:
    case SLAStatus.COMPLETED_LATE:
      return 'text-red-600 font-bold';
    case SLAStatus.NOT_APPLICABLE:
      return 'text-slate-500 italic';
    default:
      return 'text-slate-500';
  }
};

const getRepairStageStatusClass = (status: RepairStageStatus): string => {
  switch (status) {
    case RepairStageStatus.PENDING: return 'bg-slate-100 text-slate-600 border-slate-300';
    case RepairStageStatus.IN_PROGRESS: return 'bg-sky-100 text-sky-700 border-sky-300';
    case RepairStageStatus.COMPLETED: return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    case RepairStageStatus.SKIPPED: return 'bg-gray-100 text-gray-500 border-gray-300 line-through';
    default: return 'bg-slate-100 text-slate-700 border-slate-300';
  }
};


const getPriorityClass = (priority: TicketPriority): string => {
  switch (priority) {
    case TicketPriority.HIGH:
      return 'text-red-600 font-semibold';
    case TicketPriority.MEDIUM:
      return 'text-[var(--color-accent)] font-semibold';
    case TicketPriority.LOW:
      return 'text-emerald-600 font-semibold';
    default:
      return 'text-slate-600 font-semibold';
  }
};

const getPaymentStatusClass = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.PAID:
      return 'text-emerald-600';
    case PaymentStatus.UNPAID:
      return 'text-red-600';
    case PaymentStatus.PENDING:
      return 'text-amber-600';
    case PaymentStatus.REFUNDED:
      return 'text-slate-600';
    default:
      return 'text-slate-500';
  }
};

// Icons
const CalendarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
  </svg>
);
const DeviceMobileIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path fillRule="evenodd" d="M6.5 3A1.5 1.5 0 005 4.5v11A1.5 1.5 0 006.5 17h7a1.5 1.5 0 001.5-1.5v-11A1.5 1.5 0 0013.5 3h-7zM6 4.5A.5.5 0 016.5 4h7a.5.5 0 01.5.5v11a.5.5 0 01-.5.5h-7a.5.5 0 01-.5-.5v-11zM9 14a1 1 0 102 0 1 1 0 00-2 0z" clipRule="evenodd" />
  </svg>
);
const MailIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
    <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
  </svg>
);
const UserIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z" clipRule="evenodd" />
  </svg>
);
const CreditCardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path d="M2.75 4A2.75 2.75 0 015.5 1.25h9A2.75 2.75 0 0117.25 4v2.5H2.75V4z" />
    <path fillRule="evenodd" d="M2.75 7.75v8A2.75 2.75 0 005.5 18.5h9a2.75 2.75 0 002.75-2.75v-8H2.75zm4.5 6.5a.75.75 0 000-1.5H5.5a.75.75 0 000 1.5h1.75z" clipRule="evenodd" />
  </svg>
);
const LoadingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg className="animate-spin h-4 w-4 text-[var(--color-primary)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" {...props}>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);
const ErrorIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1 text-red-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);
const TagIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-[var(--color-primary)]" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
  </svg>
);
const EyeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const ChevronDownIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-1" {...props}>
    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clipRule="evenodd" />
  </svg>
);
const PencilSquareIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);
const ListBulletIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
 </svg>
);
const SparklesIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-purple-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L1.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.25 12L17 14.187l-1.25-2.187a3.375 3.375 0 00-2.682-2.682L10.813 9l2.187-1.25a3.375 3.375 0 002.682-2.682L17 1.813l1.25 2.187a3.375 3.375 0 002.682 2.682L23.187 9l-2.187 1.25a3.375 3.375 0 00-2.682 2.682z" transform="scale(0.83) translate(2,2)" />
  </svg>
);
const DocumentTextIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);
const CheckBadgeIconTicket: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
  </svg>
);
const ChatBubbleLeftEllipsisIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-3.862 8.25-8.625 8.25CF7.836 20.25 4.5 16.911 4.5 12.284c0-1.948.786-3.743 2.086-5.044l.48-1.202a.75.75 0 01.68-.529h3.196a.75.75 0 01.608.328l.832 1.248A8.196 8.196 0 0112.375 6C17.138 6 21 9.744 21 12z" />
  </svg>
);
const PhoneIconTicket: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.051-.986-.15-1.433l-2.086-6.257a2.25 2.25 0 00-2.317-1.642l-2.23.892a2.25 2.25 0 01-2.434-.605l-2.132-2.132a2.25 2.25 0 00-1.604-.605h-1.132a2.25 2.25 0 00-2.25 2.25v2.132M4.5 19.5h15M12 4.5v.01" />
  </svg>
);
const EnvelopeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);
const CogIconTab: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l-1.41-.513M3 10.5h18M4.5 4.5l.47.187m14.554 9.626l-.47.187M9.75 21a8.25 8.25 0 0010.607-6.75M9.75 3a8.25 8.25 0 0110.607 6.75" />
  </svg>
);
const Bars3BottomLeftIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-slate-700" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
  </svg>
);
const WrenchScrewdriverIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.472-2.472a3.375 3.375 0 00-4.773-4.773L4.75 10.5M11.42 15.17L4.75 10.5m6.67 4.67l-2.472-2.472a3.375 3.375 0 00-4.773-4.773L4.75 10.5M11.42 15.17L4.75 10.5" />
  </svg>
);
const ShieldCheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);
const ListCheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12H4m4.5 0H12m4.5 0H20M3.75 6.75H4m4.5 0H12m4.5 0H20m-16.5 8.5H4m4.5 0H12m4.5 0H20" />
  </svg>
);
const CalendarDaysIconTicket: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5M12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
  </svg>
);
const ArchiveBoxArrowDownIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 align-text-bottom" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-1.541 8.658a2.25 2.25 0 01-2.223 1.842H7.516a2.25 2.25 0 01-2.222-1.842L3.75 7.5M10.5 11.25h3M3.75 7.5h16.5" />
  </svg>
);
const BookOpenIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1 text-purple-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);
const BeakerIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355-.186-.676-.401-.959a1.984 1.984 0 00-1.605-1.045 1.983 1.983 0 00-1.604 1.045 1.11 1.11 0 00-.401.959v1.413h4.01V6.087z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 12.75L11.25 12.75M12.75 12.75V6.375V5.625C12.75 4.904 12.196 4.35 11.475 4.35H8.525C7.804 4.35 7.25 4.904 7.25 5.625V6.375m5.5 6.375v3.375c0 .621-.504 1.125-1.125 1.125H8.625c-.621 0-1.125-.504-1.125-1.125V12.75m5.5 0H7.25m.75-6.375C6.186 6.375 5.25 7.312 5.25 8.438V10.5c0 1.125.936 2.062 2.063 2.062H8.25v2.063c0 1.125.936 2.062 2.063 2.062H13.5c1.125 0 2.063-.937 2.063-2.062V14.812M12 12.75h1.5M12 12.75H7.5" />
    </svg>
  );
const ExclamationTriangleIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-amber-500" {...props}>
    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
  </svg>
);

const getCommunicationTypeIcon = (type: CommunicationType) => {
  switch (type) {
    case CommunicationType.SMS: return <ChatBubbleLeftEllipsisIcon />;
    case CommunicationType.EMAIL: return <EnvelopeIcon />;
    case CommunicationType.CALL: return <PhoneIconTicket />;
    case CommunicationType.SYSTEM: return <CogIconTab />;
    case CommunicationType.CUSTOMER_PORTAL: return <EyeIcon className="w-3.5 h-3.5 mr-1 text-slate-500"/>;
    default: return <ChatBubbleLeftEllipsisIcon />;
  }
};

type AiSummaryState = 'idle' | 'loading' | 'success' | 'error';
type AiPartsState = 'idle' | 'loading' | 'success' | 'error';
type AiCustomerSummaryState = 'idle' | 'loading' | 'success' | 'error';
type AiKnowledgeBaseState = 'idle' | 'loading' | 'success' | 'error';
type ActiveTabType = 'notes' | 'comms' | 'progress_actions' | 'parts' | 'checklist' | 'ai_tools';

interface SuggestedPart {
  name: string;
  type?: 'part' | 'tool' | 'supply';
  reason?: string;
}

export const TicketItem: React.FC<TicketItemProps> = ({
    ticket,
    appSettings,
    currentUser,
    inventoryItems,
    onOpenUpdateStatusModal,
    onViewCustomerProfile,
    onOpenPaymentModal,
    onOpenPrintModal,
    onShowCustomerPortal,
    onAddInternalTicketNote,
    onUpdateRepairStage,
    onAddCommunicationLog,
    onOpenAppointmentModal,
    onAddPartToTicket,
    repairChecklists,
    onUpdateTicketChecklistProgress,
}) => {
  const [newInternalNote, setNewInternalNote] = useState('');
  const [prefillNoteText, setPrefillNoteText] = useState<string | null>(null); // New state for pre-filling
  const [editingStageNotes, setEditingStageNotes] = useState<Record<string, string>>({});
  const [isCheckInProcessModalOpen, setIsCheckInProcessModalOpen] = useState(false);

  const [aiInternalNotesSummaryState, setAiInternalNotesSummaryState] = useState<AiSummaryState>('idle');
  const [aiInternalNotesSummaryText, setAiInternalNotesSummaryText] = useState<string | null>(null);
  const [isAiInternalNotesSummaryVisible, setIsAiInternalNotesSummaryVisible] = useState(false);

  const [aiPartsState, setAiPartsState] = useState<AiPartsState>('idle');
  const [suggestedPartsList, setSuggestedPartsList] = useState<SuggestedPart[]>([]);
  const [partsSuggestionError, setPartsSuggestionError] = useState<string | null>(null);

  const [aiCustomerSummaryState, setAiCustomerSummaryState] = useState<AiCustomerSummaryState>('idle');
  const [aiCustomerSummaryText, setAiCustomerSummaryText] = useState<string>('');
  const [aiCustomerSummaryError, setAiCustomerSummaryError] = useState<string | null>(null);

  const [isSolutionsModalOpen, setIsSolutionsModalOpen] = useState(false);
  const [aiKnowledgeBaseState, setAiKnowledgeBaseState] = useState<AiKnowledgeBaseState>('idle');
  const [aiKnowledgeBaseSolutions, setAiKnowledgeBaseSolutions] = useState<string | null>(null);
  const [aiKnowledgeBaseError, setAiKnowledgeBaseError] = useState<string | null>(null);

  const [selectedPartId, setSelectedPartId] = useState<string>('');
  const [partQuantity, setPartQuantity] = useState<number>(1);
  const [addPriceToTotal, setAddPriceToTotal] = useState<boolean>(true);
  const [showAddPartForm, setShowAddPartForm] = useState<boolean>(false);

  const apiKey = useRef(getApiKey());
  const internalNoteTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [newLogType, setNewLogType] = useState<CommunicationType>(CommunicationType.CALL);
  const [newLogContent, setNewLogContent] = useState('');
  const [newLogRecipient, setNewLogRecipient] = useState('');
  const [newLogStaffName, setNewLogStaffName] = useState(currentUser?.name || 'Staff');

  const [showDetailsTabs, setShowDetailsTabs] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTabType>('notes');

  const isUserInactive = currentUser?.status === 'Inactive';
  const canProcessPayment = currentUser?.role !== 'Technician' && !isUserInactive;
  const canManageWorkflow = !isUserInactive;
  const canUseAITools = !isUserInactive && !!apiKey.current;
  const canManageParts = !isUserInactive;

  const availableInventoryForTicket = useMemo(() => {
    return inventoryItems.filter(item => item.usageType === 'repair' || item.usageType === 'general');
  }, [inventoryItems]);

  useEffect(() => {
    if (prefillNoteText && internalNoteTextareaRef.current) {
        setNewInternalNote(prefillNoteText);
        internalNoteTextareaRef.current.focus();
        setPrefillNoteText(null); // Clear after pre-filling
    }
  }, [prefillNoteText]);

  useEffect(() => {
    setAiInternalNotesSummaryState('idle');
    setAiInternalNotesSummaryText(null);
    setIsAiInternalNotesSummaryVisible(false);
  }, [ticket.internalNotes]);

  const formatDate = (dateString?: string, includeTime: boolean = true) => {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric', month: 'short', day: 'numeric'
    };
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const fetchAiInternalNotesSummary = useCallback(async () => {
    if (!ticket.internalNotes || ticket.internalNotes.length === 0) {
      setAiInternalNotesSummaryText("No internal notes to summarize.");
      setAiInternalNotesSummaryState('error');
      setIsAiInternalNotesSummaryVisible(true);
      return;
    }

    if (!apiKey.current) {
      setAiInternalNotesSummaryText("AI Summarization disabled: API Key not configured.");
      setAiInternalNotesSummaryState('error');
      setIsAiInternalNotesSummaryVisible(true);
      return;
    }

    setAiInternalNotesSummaryState('loading');
    setAiInternalNotesSummaryText(null);
    setIsAiInternalNotesSummaryVisible(true);

    const notesText = ticket.internalNotes.map(note => `${note.createdBy} (${formatDate(note.createdAt)}): ${note.text}`).join("\n---\n");
    const prompt = `Summarize these technician notes for a quick overview of a support ticket. Focus on key actions, findings, and current status. Be concise. Notes:\n\n${notesText}`;

    try {
      const ai = new GoogleGenAI({ apiKey: apiKey.current });
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-04-17",
        contents: prompt,
      });
      setAiInternalNotesSummaryText(response.text);
      setAiInternalNotesSummaryState('success');
    } catch (error) {
      console.error("Error generating internal notes summary:", error);
      let errorMessage = "Failed to generate AI summary.";
      if (error instanceof Error) errorMessage += ` Details: ${error.message}`;
      setAiInternalNotesSummaryText(errorMessage);
      setAiInternalNotesSummaryState('error');
    }
  }, [ticket.internalNotes, apiKey]);

  const handleSuggestParts = useCallback(async () => {
    if (!apiKey.current) {
        setPartsSuggestionError("AI Parts Suggestion disabled: API Key not configured.");
        setAiPartsState('error');
        return;
    }

    setAiPartsState('loading');
    setSuggestedPartsList([]);
    setPartsSuggestionError(null);

    const promptContent = `Ticket Subject: ${ticket.subject}\nDescription: ${ticket.description}\nDevice Model: ${ticket.deviceModel || 'Not specified'}\nCategory: ${ticket.category || 'Not specified'}\n\nBased on the above ticket information, suggest potential parts and common tools needed for this type of repair. Return the response as a JSON array of objects, where each object has a "name" (string) and optionally "type" (string, e.g., "part", "tool", "supply") and "reason" (string, brief reason). Example: [{"name": "iPhone 12 Screen Assembly", "type": "part"}, {"name": "P2 Pentalobe Screwdriver", "type": "tool", "reason": "For opening iPhone"}]`;

    try {
        const ai = new GoogleGenAI({ apiKey: apiKey.current });
        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash-preview-04-17",
            contents: promptContent,
            config: { responseMimeType: "application/json" }
        });

        let jsonStr = response.text.trim();
        const fenceRegex = /^```(\w*)?\s*\n?(.*?)\n?\s*```$/s;
        const match = jsonStr.match(fenceRegex);
        if (match && match[2]) jsonStr = match[2].trim();

        try {
            const parsedData = JSON.parse(jsonStr);
            if (Array.isArray(parsedData) && parsedData.every(item => typeof item.name === 'string')) {
                setSuggestedPartsList(parsedData as SuggestedPart[]);
                setAiPartsState('success');
            } else {
                throw new Error("AI returned data in an unexpected format. Expected array of objects with 'name'.");
            }
        } catch (parseError) {
            console.error("Failed to parse JSON response for parts:", parseError, "Raw response:", jsonStr);
            setPartsSuggestionError("AI returned data in an unexpected format. Could not parse suggestions.");
            setAiPartsState('error');
        }

    } catch (error) {
        console.error("Error fetching AI parts suggestions:", error);
        let errorMessage = "Failed to get AI parts suggestions.";
         if (error instanceof Error) errorMessage += ` Details: ${error.message}`;
        setPartsSuggestionError(errorMessage);
        setAiPartsState('error');
    }
  }, [ticket.subject, ticket.description, ticket.deviceModel, ticket.category, apiKey]);

  const handleGenerateCustomerSummary = useCallback(async () => {
    if (!apiKey.current) {
        setAiCustomerSummaryError("AI Customer Summary disabled: API Key not configured.");
        setAiCustomerSummaryState('error');
        return;
    }
    setAiCustomerSummaryState('loading');
    setAiCustomerSummaryText('');
    setAiCustomerSummaryError(null);

    const internalNotesText = ticket.internalNotes.map(note => `- ${note.text} (by ${note.createdBy})`).join("\n");
    const prompt = `Generate a customer-friendly summary for ticket ${ticket.id}.
Customer: ${ticket.customer}
Device: ${ticket.deviceModel || 'N/A'}
Reported Issue: ${ticket.subject}
Issue Details: ${ticket.description}
---
Technician's Internal Notes (summary of work performed):
${internalNotesText || "No specific internal notes detailing work completed."}
---
Based on all the above, write a polite, non-technical summary of the work performed for the customer. Address the customer by name. If specific parts were replaced or key actions taken (based on internal notes), mention them simply.
`;

    try {
      const ai = new GoogleGenAI({ apiKey: apiKey.current });
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-04-17",
        contents: prompt,
      });
      setAiCustomerSummaryText(response.text);
      setAiCustomerSummaryState('success');
    } catch (error) {
      console.error("Error generating AI customer summary:", error);
      let errorMessage = "Failed to generate AI customer summary.";
      if (error instanceof Error) errorMessage += ` Details: ${error.message}`;
      setAiCustomerSummaryError(errorMessage);
      setAiCustomerSummaryState('error');
    }
  }, [ticket, apiKey]);

  const handleSearchKnowledgeBase = useCallback(async () => {
    if (!apiKey.current) {
      setAiKnowledgeBaseError("AI Knowledge Base Search disabled: API Key not configured.");
      setAiKnowledgeBaseState('error');
      setIsSolutionsModalOpen(true);
      return;
    }

    setAiKnowledgeBaseState('loading');
    setAiKnowledgeBaseSolutions(null);
    setAiKnowledgeBaseError(null);
    setIsSolutionsModalOpen(true);

    const prompt = `You are an expert knowledge base search assistant for tech repair.
Based on the following ticket, provide relevant troubleshooting steps, known issues, or repair guides.
Ticket ID: ${ticket.id}
Device Model: ${ticket.deviceModel || 'Not specified'}
Subject: ${ticket.subject}
Description: ${ticket.description}

Search a comprehensive (simulated) knowledge base and provide concise, actionable information.
Format the response clearly using markdown for lists, bolding, etc. If multiple distinct solutions or articles are found, separate them.`;

    try {
      const ai = new GoogleGenAI({ apiKey: apiKey.current });
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-preview-04-17",
        contents: prompt,
      });
      setAiKnowledgeBaseSolutions(response.text);
      setAiKnowledgeBaseState('success');
    } catch (error) {
      console.error("Error searching knowledge base with AI:", error);
      let errorMessage = "Failed to get AI knowledge base results.";
      if (error instanceof Error) errorMessage += ` Details: ${error.message}`;
      setAiKnowledgeBaseError(errorMessage);
      setAiKnowledgeBaseState('error');
    }
  }, [ticket, apiKey]);


  const handleCopyCustomerSummary = () => {
    if (aiCustomerSummaryText) {
        navigator.clipboard.writeText(aiCustomerSummaryText)
            .then(() => alert("Summary copied to clipboard!"))
            .catch(err => alert("Failed to copy summary."));
    }
  };

  const handleLogCustomerSummary = () => {
    if (aiCustomerSummaryText) {
        onAddCommunicationLog(ticket.id, CommunicationType.EMAIL, `Customer Repair Summary (AI Generated):\n${aiCustomerSummaryText}`, ticket.customerEmail, currentUser?.name || "System AI");
        alert("Summary logged to Communications Log.");
    }
  };

  const handleAiInternalNotesSummaryButtonClick = () => {
    if (aiInternalNotesSummaryState === 'loading') return;
    if (aiInternalNotesSummaryState === 'error' || !isAiInternalNotesSummaryVisible) fetchAiInternalNotesSummary();
    else setIsAiInternalNotesSummaryVisible(false);
  };

  const getAiInternalNotesSummaryButtonText = () => {
    if (aiInternalNotesSummaryState === 'loading') return "Loading AI Summary...";
    if (aiInternalNotesSummaryState === 'error') return "Retry AI Summary";
    if (aiInternalNotesSummaryState === 'success' && isAiInternalNotesSummaryVisible) return "Hide AI Summary";
    if (aiInternalNotesSummaryState === 'success' && !isAiInternalNotesSummaryVisible && aiInternalNotesSummaryText) return "Show AI Summary";
    return "Get AI Summary";
  };

  const handleCustomerNameClick = () => {
    if (onViewCustomerProfile) {
      onViewCustomerProfile(ticket.customer);
    }
  };

  const handlePrintDeviceLabel = () => {
    onOpenPrintModal(`Device Label - ${ticket.id}`, <PrintableDeviceLabel ticket={ticket} />, 'label-content-area');
  };

  const handlePrintCompletionAgreement = () => {
    onOpenPrintModal(
        `Service Completion - ${ticket.id}`,
        <PrintableCompletionAgreement ticket={ticket} appSettings={appSettings} />,
        'label-content-area'
    );
  };

  const handleOpenCheckInProcessModal = () => setIsCheckInProcessModalOpen(true);

  const canTakePaymentCondition = (
      ticket.status === TicketStatus.READY_FOR_PICKUP ||
      ticket.status === TicketStatus.COMPLETED ||
      (ticket.status === TicketStatus.IN_PROGRESS && ticket.isEstimateApproved === true)
    ) && ticket.paymentStatus === PaymentStatus.UNPAID && ticket.amountDue > 0;

  const handleAddInternalNoteSubmit = () => {
    if (isUserInactive) {
      alert("Inactive users cannot add notes.");
      return;
    }
    if (newInternalNote.trim()) {
      onAddInternalTicketNote(ticket.id, newInternalNote.trim(), currentUser?.name || "Staff");
      setNewInternalNote('');
    }
  };

  const handleStageNoteChange = (stageId: string, text: string) => {
    setEditingStageNotes(prev => ({ ...prev, [stageId]: text }));
  };

  const handleSaveStageNote = (stageId: string) => {
    if (!canManageWorkflow) {
        alert("You do not have permission to update repair stages.");
        return;
    }
    const noteText = editingStageNotes[stageId];
    const stage = ticket.repairProgress.find(s => s.id === stageId);
    if (stage && typeof noteText === 'string') {
      onUpdateRepairStage(ticket.id, stageId, stage.status, noteText);
      setEditingStageNotes(prev => ({ ...prev, [stageId]: stage.notes || ''})); // Keep displayed note consistent
    }
  };

  const handleRepairStageStatusChange = (stageId: string, newStatus: RepairStageStatus) => {
    if (!canManageWorkflow) {
        alert("You do not have permission to update repair stages.");
        return;
    }
     const currentNote = editingStageNotes[stageId] || ticket.repairProgress.find(s => s.id === stageId)?.notes;
     onUpdateRepairStage(ticket.id, stageId, newStatus, currentNote);
  };

  const handleAddCommunicationLogEntry = () => {
    if (isUserInactive) {
      alert("Inactive users cannot add communication logs.");
      return;
    }
    if (newLogContent.trim()) {
      onAddCommunicationLog(
        ticket.id,
        newLogType,
        newLogContent.trim(),
        newLogRecipient.trim() || undefined,
        newLogStaffName.trim() || currentUser?.name || "Staff"
      );
      setNewLogContent('');
      setNewLogRecipient('');
    }
  };

  const handleAddPartFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPartId || partQuantity <= 0) {
        alert("Please select a part and specify a valid quantity.");
        return;
    }
    if (canManageParts) {
        onAddPartToTicket(ticket.id, selectedPartId, partQuantity, addPriceToTotal);
        setSelectedPartId('');
        setPartQuantity(1);
        setAddPriceToTotal(true);
        setShowAddPartForm(false);
    } else {
        alert("You do not have permission to add parts.");
    }
  };

  const currentChecklist = useMemo(() => {
    if (ticket.activeChecklistId) {
      const found = repairChecklists.find(cl => cl.id === ticket.activeChecklistId);
      if (found) return found;
    }
    // Fallback: Try to find a checklist based on ticket category if no active one is set
    if (ticket.category) {
        const categoryMatch = repairChecklists.find(cl => cl.associatedCategory?.toLowerCase() === ticket.category?.toLowerCase());
        if (categoryMatch) return categoryMatch;
    }
    return null;
  }, [ticket.activeChecklistId, ticket.category, repairChecklists]);

  const handleChecklistItemToggle = (itemId: string, completed: boolean) => {
    onUpdateTicketChecklistProgress(ticket.id, itemId, completed);
  };

  const handleOpenAppointmentModalForTicket = () => {
    onOpenAppointmentModal({
        ticketId: ticket.id,
        customerName: ticket.customer,
        date: new Date().toISOString().split('T')[0]
    });
  };

  const tabButtonStyle = (isActive: boolean) =>
    `py-2 px-4 text-sm font-medium border-b-2 focus:outline-none flex items-center
     ${isActive
       ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
       : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
     }`;

  // Recommended Actions Logic (Enhanced)
  const renderRecommendedActions = () => {
    const actions = [];
    const currentRepairStage = ticket.repairProgress.find(stage => stage.status === RepairStageStatus.IN_PROGRESS);

    if (ticket.status === TicketStatus.NEW || currentRepairStage?.id === 'diagnosis') {
        actions.push(
            <Button key="log-diag" variant="outline" className="text-xs !px-2 !py-1" onClick={() => {
                setActiveTab('notes');
                setPrefillNoteText(`Initial Diagnosis for ${ticket.deviceModel || 'device'} (Ticket ${ticket.id}):\nVisual Inspection:\nCustomer Reported Symptoms Verified:\nInitial Diagnostic Steps Taken:\nNext Steps:`);
            }}>Log Initial Diagnosis Findings</Button>,
            <Button key="search-kb" variant="outline" className="text-xs !px-2 !py-1" onClick={() => {setActiveTab('ai_tools'); handleSearchKnowledgeBase();}}>Search Knowledge Base</Button>
        );
    }
    if (ticket.status === TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL) {
        actions.push(
            <div key="approval-info" className="text-xs text-slate-600 p-2 bg-amber-50 border border-amber-200 rounded-md">
                Estimate: <span className="font-semibold">${ticket.amountDue.toFixed(2)}</span> | Approved: <span className="font-semibold">{ticket.isEstimateApproved === true ? 'Yes' : ticket.isEstimateApproved === false ? 'No' : 'Pending'}</span>
            </div>,
            <Button key="log-comm-approval" variant="outline" className="text-xs !px-2 !py-1" onClick={() => setActiveTab('comms')}>Log Customer Communication</Button>,
            <Button key="approve-manual" variant="outline" className="text-xs !px-2 !py-1" onClick={() => {
                if(window.confirm("Manually mark estimate as approved?")){
                    onUpdateRepairStage(ticket.id, 'approval_wait', RepairStageStatus.COMPLETED, "Customer approved manually.");
                    onOpenUpdateStatusModal(ticket.id, TicketStatus.IN_PROGRESS);
                }
            }}>Mark Estimate Approved (Manual)</Button>
        );
        if (onShowCustomerPortal) {
            actions.push(
                <Button key="copy-portal-link" variant="outline" className="text-xs !px-2 !py-1" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}?view=customerPortal&ticketId=${ticket.id}`);
                    alert("Customer Portal link copied to clipboard!");
                }}>Copy Portal Link</Button>
            );
        }
    }
    if (ticket.status === TicketStatus.IN_PROGRESS && currentRepairStage?.id === 'repairing') {
        actions.push(
            <Button key="add-part" variant="outline" className="text-xs !px-2 !py-1" onClick={() => {setActiveTab('parts'); setShowAddPartForm(true);}}>Add Part Used</Button>
        );
        if (currentChecklist) { // Changed from ticket.activeChecklistId
            actions.push(<Button key="update-cl" variant="outline" className="text-xs !px-2 !py-1" onClick={() => setActiveTab('checklist')}>Update Checklist Item</Button>);
        }
    }
    if (ticket.status === TicketStatus.READY_FOR_PICKUP) {
        if (canTakePaymentCondition && canProcessPayment) {
            actions.push(<Button key="take-payment-ready" variant="secondary" className="text-xs !px-2 !py-1" onClick={() => onOpenPaymentModal(ticket.id, ticket.amountDue, ticket.customer)}>Take Payment (${ticket.amountDue.toFixed(2)})</Button>);
        }
        actions.push(<Button key="print-complete-agree" variant="outline" className="text-xs !px-2 !py-1" onClick={handlePrintCompletionAgreement}>Print Completion Agreement</Button>);
    }

    const slaRisk = ticket.responseSlaStatus === SLAStatus.AT_RISK || ticket.responseSlaStatus === SLAStatus.BREACHED ||
                    ticket.resolutionSlaStatus === SLAStatus.AT_RISK || ticket.resolutionSlaStatus === SLAStatus.BREACHED;

    return (
        <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-md">
            <h4 className="text-xs font-semibold text-sky-800 mb-2">Recommended Actions:</h4>
            {slaRisk && (
                <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded-md text-xs text-amber-700 font-medium flex items-center">
                    <ExclamationTriangleIconSolid className="w-4 h-4 mr-1.5 text-amber-600" />
                    <span>SLA Warning:</span>
                    {ticket.responseSlaStatus === SLAStatus.AT_RISK && <span className="ml-1">Response SLA at risk.</span>}
                    {ticket.responseSlaStatus === SLAStatus.BREACHED && <span className="ml-1">Response SLA breached!</span>}
                    {ticket.resolutionSlaStatus === SLAStatus.AT_RISK && <span className="ml-1">Resolution SLA at risk.</span>}
                    {ticket.resolutionSlaStatus === SLAStatus.BREACHED && <span className="ml-1">Resolution SLA breached!</span>}
                </div>
            )}
            {actions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {actions}
                </div>
            ) : <p className="text-xs text-slate-500 italic">No specific actions recommended at this stage.</p>}
        </div>
    );
  };


  return (
    <>
    <div className="bg-white shadow-lg rounded-lg p-5 transition-all hover:shadow-xl border border-slate-200">
       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
        <h3 className="text-lg font-semibold text-slate-800 mr-2">
          {ticket.id}: {ticket.subject}
        </h3>
        <span className={`px-3 py-1 text-xs font-bold rounded-full border ${getStatusClass(ticket.status)}`}>
          {ticket.status}
        </span>
      </div>

      <p className="text-sm text-slate-600 mb-4 leading-relaxed">{ticket.description}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm mb-3">
        <div>
          <span className="text-slate-500 font-medium">Priority: </span>
          <span className={getPriorityClass(ticket.priority)}>{ticket.priority}</span>
          {ticket.aiSuggestedPriority && ticket.aiSuggestedPriority !== ticket.priority && (
            <span className="text-xs text-purple-500 ml-1" title={`AI Suggested: ${ticket.aiSuggestedPriority}`}>(AI: {ticket.aiSuggestedPriority})</span>
          )}
        </div>
        {ticket.category && (
          <div>
            <span className="text-slate-500 font-medium">Category: </span>
            <span className="text-slate-700">{ticket.category}</span>
            {ticket.aiSuggestedCategory && ticket.aiSuggestedCategory !== ticket.category && (
              <span className="text-xs text-purple-500 ml-1 truncate" title={`AI Suggested: ${ticket.aiSuggestedCategory}`}>(AI: {ticket.aiSuggestedCategory.substring(0,15)+(ticket.aiSuggestedCategory.length > 15 ? '...':'')})</span>
            )}
          </div>
        )}
        <div className="flex items-center">
          <UserIcon />
          <span className="text-slate-500 font-medium mr-1">Customer:</span>
          {onViewCustomerProfile ? (
            <button onClick={handleCustomerNameClick} className="text-[var(--color-primary)] hover:brightness-90 hover:underline font-medium transition-colors">
              {ticket.customer}
            </button>
          ) : (
            <span className="text-slate-700">{ticket.customer}</span>
          )}
        </div>
        {ticket.customerPhoneNumber && (
          <div className="flex items-center">
            <DeviceMobileIcon />
            <span className="text-slate-500 font-medium mr-1">Phone:</span>
            <span className="text-slate-700">{ticket.customerPhoneNumber}</span>
          </div>
        )}
        {ticket.customerEmail && (
          <div className="flex items-center sm:col-span-2 md:col-span-1">
            <MailIcon />
            <span className="text-slate-500 font-medium mr-1">Email:</span>
            <span className="text-slate-700 truncate" title={ticket.customerEmail}>{ticket.customerEmail}</span>
          </div>
        )}
         {ticket.deviceModel && (
          <div>
            <span className="text-slate-500 font-medium">Device: </span>
            <span className="text-slate-700">{ticket.deviceModel} {ticket.deviceSerialNumber && `(SN: ${ticket.deviceSerialNumber})`}</span>
          </div>
        )}
        <div>
          <span className="text-slate-500 font-medium">Assigned To: </span>
          <span className="text-slate-700">{ticket.assignedTo || 'Unassigned'}</span>
        </div>
        {ticket.dataBackupStatus && ticket.dataBackupStatus !== 'Unknown' && (
          <div>
            <span className="text-slate-500 font-medium">Backed Up: </span>
            <span className="text-slate-700">{ticket.dataBackupStatus}</span>
          </div>
        )}
        {ticket.findMyDeviceDisabled && ticket.findMyDeviceDisabled !== 'Unknown' && (
          <div>
            <span className="text-slate-500 font-medium">Find My Disabled: </span>
            <span className="text-slate-700">{ticket.findMyDeviceDisabled}</span>
          </div>
        )}
        <div className="flex items-center">
          <CreditCardIcon />
          <span className="text-slate-500 font-medium mr-1">Amount Due:</span>
          <span className="text-slate-700 font-semibold">${ticket.amountDue.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-slate-500 font-medium">Payment: </span>
          <span className={`${getPaymentStatusClass(ticket.paymentStatus)} font-semibold`}>{ticket.paymentStatus}</span>
        </div>
      </div>

      {(ticket.respondByDate || ticket.resolveByDate) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3 p-3 border border-slate-200 rounded-md bg-slate-50">
            {ticket.respondByDate && (
              <div>
                <span className="text-slate-500 font-medium">Respond By: </span>
                <span className={getSLAStatusClass(ticket.responseSlaStatus)}>
                    {formatDate(ticket.respondByDate)} ({ticket.responseSlaStatus || 'N/A'})
                </span>
              </div>
            )}
            {ticket.resolveByDate && (
              <div>
                <span className="text-slate-500 font-medium">Resolve By: </span>
                <span className={getSLAStatusClass(ticket.resolutionSlaStatus)}>
                    {formatDate(ticket.resolveByDate)} ({ticket.resolutionSlaStatus || 'N/A'})
                </span>
              </div>
            )}
        </div>
      )}

      <div className="mt-4 border-t border-slate-200 pt-3">
        <button
          onClick={() => setShowDetailsTabs(!showDetailsTabs)}
          className="w-full flex justify-between items-center text-left py-2.5 px-3 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-medium text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          aria-expanded={showDetailsTabs}
        >
          <span className="flex items-center"><Bars3BottomLeftIcon className="mr-2"/> Manage Ticket Details</span>
          <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${showDetailsTabs ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showDetailsTabs && (
        <div className="mt-3 p-3 bg-slate-50 rounded-md border border-slate-200">
          <div className="flex border-b border-slate-300 mb-3 -mx-3 px-1 flex-wrap">
            <button onClick={() => setActiveTab('notes')} className={tabButtonStyle(activeTab === 'notes')}>
              <PencilSquareIcon /> Internal Notes ({ticket.internalNotes?.length || 0})
            </button>
            <button onClick={() => setActiveTab('comms')} className={tabButtonStyle(activeTab === 'comms')}>
              <ChatBubbleLeftEllipsisIcon /> Comms Log ({ticket.communicationLog?.length || 0})
            </button>
            <button onClick={() => setActiveTab('progress_actions')} className={tabButtonStyle(activeTab === 'progress_actions')}>
              <ListBulletIcon /> Progress & Actions
            </button>
            <button onClick={() => setActiveTab('parts')} className={tabButtonStyle(activeTab === 'parts')}>
              <ArchiveBoxArrowDownIcon /> Parts Used ({ticket.partsUsed?.length || 0})
            </button>
            <button onClick={() => setActiveTab('checklist')} className={tabButtonStyle(activeTab === 'checklist')}>
              <ListCheckIcon /> Repair Checklist
            </button>
            <button onClick={() => setActiveTab('ai_tools')} className={tabButtonStyle(activeTab === 'ai_tools')}>
              <SparklesIcon /> AI Tools
            </button>
          </div>

          <div>
            {activeTab === 'notes' && (
              <div className="space-y-3">
                 {ticket.internalNotes && ticket.internalNotes.length > 0 && (
                  <Button
                    onClick={handleAiInternalNotesSummaryButtonClick}
                    disabled={aiInternalNotesSummaryState === 'loading' || !canUseAITools}
                    variant="ghost"
                    className="text-xs py-1 px-2 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 flex items-center mb-2"
                    title={!canUseAITools ? "AI tools disabled (API key missing or user inactive)" : ""}
                  >
                    <SparklesIcon className="mr-1 w-3.5 h-3.5" /> {getAiInternalNotesSummaryButtonText()}
                  </Button>
                )}
                 {isAiInternalNotesSummaryVisible && (
                  <div className="mb-3 p-3 bg-white rounded-md border border-slate-200 shadow-sm">
                    <h5 className="text-xs font-semibold text-slate-600 mb-1 flex items-center"><SparklesIcon className="mr-1 w-3 h-3"/>AI Generated Summary:</h5>
                    {aiInternalNotesSummaryState === 'loading' && (
                      <div className="flex items-center text-xs text-slate-500">
                        <LoadingIcon /> <span className="ml-1">Loading summary...</span>
                      </div>
                    )}
                    {aiInternalNotesSummaryState === 'error' && aiInternalNotesSummaryText && (
                      <div className="flex items-center text-xs text-red-500">
                        <ErrorIcon className="w-3.5 h-3.5"/> <span className="ml-1">{aiInternalNotesSummaryText}</span>
                      </div>
                    )}
                    {aiInternalNotesSummaryState === 'success' && aiInternalNotesSummaryText && (
                      <p className="text-xs text-slate-600 italic whitespace-pre-wrap">{aiInternalNotesSummaryText}</p>
                    )}
                  </div>
                )}
                {ticket.internalNotes && ticket.internalNotes.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                    {ticket.internalNotes.map(note => (
                      <div key={note.id} className="text-xs p-2 bg-white border border-slate-200 rounded shadow-sm">
                        <p className="text-slate-700 whitespace-pre-wrap">{note.text}</p>
                        <p className="text-slate-500 mt-1">
                          By: {note.createdBy} - {formatDate(note.createdAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic py-2">No internal notes yet.</p>
                )}
                <div className="mt-2">
                  <textarea
                    ref={internalNoteTextareaRef}
                    value={newInternalNote}
                    onChange={(e) => setNewInternalNote(e.target.value)}
                    placeholder="Add an internal note..."
                    rows={2}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                    disabled={isUserInactive}
                  />
                  <Button
                    onClick={handleAddInternalNoteSubmit}
                    variant="ghost"
                    className="text-xs mt-1 py-1 px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                    disabled={!newInternalNote.trim() || isUserInactive}
                    title={isUserInactive ? "Inactive users cannot add notes" : ""}
                  >
                    Add Internal Note
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'comms' && (
              <div className="space-y-3">
                {ticket.communicationLog && ticket.communicationLog.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-2 mb-3">
                    {ticket.communicationLog.map(log => (
                      <div key={log.id} className="text-xs p-2 bg-white border border-slate-200 rounded shadow-sm">
                        <div className="flex items-center mb-1">
                          <span className="mr-1">{getCommunicationTypeIcon(log.type)}</span>
                          <span className="font-semibold text-slate-700">{log.type}</span>
                          <span className="text-slate-500 mx-1">-</span>
                          <span className="text-slate-500">{formatDate(log.timestamp)}</span>
                        </div>
                        <p className="text-slate-600 whitespace-pre-wrap">{log.content}</p>
                        <p className="text-slate-500 mt-1 text-[10px]">
                          {log.staffName && <span>By: {log.staffName}</span>}
                          {log.recipient && <span className="ml-2">To: {log.recipient}</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic py-2">No communication logs yet.</p>
                )}
                <div className="mt-2 border-t pt-3">
                  <h4 className="text-xs font-semibold text-slate-600 mb-1">Add New Log Entry:</h4>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select
                      value={newLogType}
                      onChange={(e) => setNewLogType(e.target.value as CommunicationType)}
                      className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                      disabled={isUserInactive}
                    >
                      {Object.values(CommunicationType).map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                     <input
                      type="text"
                      value={newLogStaffName}
                      onChange={(e) => setNewLogStaffName(e.target.value)}
                      placeholder="Staff Name (e.g. Your Name)"
                      className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                      disabled={isUserInactive}
                    />
                  </div>
                   <input
                    type="text"
                    value={newLogRecipient}
                    onChange={(e) => setNewLogRecipient(e.target.value)}
                    placeholder="Recipient (optional, e.g., email/phone if not primary)"
                    className="w-full p-1.5 border border-slate-300 rounded-md text-xs mb-2 focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                    disabled={isUserInactive}
                  />
                  <textarea
                    value={newLogContent}
                    onChange={(e) => setNewLogContent(e.target.value)}
                    placeholder="Log content (e.g., Called customer, left voicemail...)"
                    rows={2}
                    className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                    disabled={isUserInactive}
                  />
                  <Button
                    onClick={handleAddCommunicationLogEntry}
                    variant="ghost"
                    className="text-xs mt-1 py-1 px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                    disabled={!newLogContent.trim() || isUserInactive}
                    title={isUserInactive ? "Inactive users cannot add logs" : ""}
                  >
                    Add Log Entry
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'progress_actions' && (
               <div className="space-y-3">
                {ticket.repairProgress?.map(stage => (
                  <div key={stage.id} className="p-3 border border-slate-200 rounded-md bg-white shadow-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold text-sm text-slate-700">{stage.name}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getRepairStageStatusClass(stage.status)}`}>
                        {stage.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mb-2">
                      {stage.startedAt && <span>Started: {formatDate(stage.startedAt)}</span>}
                      {stage.completedAt && <span className="ml-2">Completed: {formatDate(stage.completedAt)}</span>}
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {Object.values(RepairStageStatus).map(statusVal => (
                            <Button
                                key={statusVal}
                                variant={stage.status === statusVal ? 'solid' : 'ghost'}
                                onClick={() => handleRepairStageStatusChange(stage.id, statusVal)}
                                className={`text-xs !px-2 !py-1 ${stage.status === statusVal ? '!bg-[var(--color-primary)] !text-white' : '!text-slate-600 hover:!bg-slate-200'}`}
                                disabled={!canManageWorkflow}
                                title={!canManageWorkflow ? "You do not have permission to update stages" : ""}
                            >
                                {statusVal}
                            </Button>
                        ))}
                    </div>
                    <textarea
                        value={editingStageNotes[stage.id] ?? stage.notes ?? ''}
                        onChange={(e) => handleStageNoteChange(stage.id, e.target.value)}
                        placeholder="Notes for this stage (internal)..."
                        rows={1}
                        className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                        disabled={!canManageWorkflow}
                    />
                    <Button variant="ghost" onClick={() => handleSaveStageNote(stage.id)} className="text-xs mt-1 py-1 px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10" disabled={!canManageWorkflow || (editingStageNotes[stage.id] === undefined && !stage.notes)}>Save Note</Button>
                  </div>
                ))}
                {!ticket.repairProgress || ticket.repairProgress.length === 0 && (
                    <p className="text-xs text-slate-500 italic py-2">No repair workflow defined for this ticket.</p>
                )}
                {renderRecommendedActions()}
              </div>
            )}

            {activeTab === 'parts' && (
               <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Parts Used in Repair</h4>
                {ticket.partsUsed && ticket.partsUsed.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2 border-b pb-2 mb-2">
                    {ticket.partsUsed.map(part => (
                      <div key={part.id} className="text-xs p-2 bg-white border border-slate-200 rounded shadow-sm">
                        <p className="text-slate-700 font-medium">{part.itemName} (Qty: {part.quantityUsed})</p>
                        <p className="text-slate-500">
                          Cost: {part.unitCostAtTimeOfUse !== undefined ? `$${part.unitCostAtTimeOfUse.toFixed(2)}` : 'N/A'} each,
                          Price: {part.unitPriceAtTimeOfUse !== undefined ? `$${part.unitPriceAtTimeOfUse.toFixed(2)}` : 'N/A'} each
                        </p>
                        <p className="text-slate-400 text-[10px]">Added by: {part.addedBy} on {formatDate(part.addedAt)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic py-2">No parts recorded for this repair yet.</p>
                )}
                {!showAddPartForm && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowAddPartForm(true)}
                    className="text-xs mt-2 py-1 px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
                    disabled={!canManageParts}
                    title={!canManageParts ? "Permission Denied" : "Add Part"}
                  >
                    Add Part to Ticket
                  </Button>
                )}
                {showAddPartForm && (
                  <form onSubmit={handleAddPartFormSubmit} className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                    <select
                      value={selectedPartId}
                      onChange={(e) => setSelectedPartId(e.target.value)}
                      className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                      disabled={!canManageParts}
                      aria-label="Select part to add"
                    >
                      <option value="">Select a part...</option>
                      {availableInventoryForTicket.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name} (Stock: {item.stock}, SKU: {item.sku || 'N/A'})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={partQuantity}
                      onChange={(e) => setPartQuantity(parseInt(e.target.value, 10) || 1)}
                      min="1"
                      className="w-full p-1.5 border border-slate-300 rounded-md text-xs focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                      placeholder="Quantity"
                      disabled={!canManageParts}
                      aria-label="Part quantity"
                    />
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`addPrice-${ticket.id}`}
                        checked={addPriceToTotal}
                        onChange={(e) => setAddPriceToTotal(e.target.checked)}
                        className="h-3.5 w-3.5 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
                        disabled={!canManageParts}
                        aria-labelledby={`addPriceLabel-${ticket.id}`}
                      />
                      <label htmlFor={`addPrice-${ticket.id}`} id={`addPriceLabel-${ticket.id}`} className="ml-2 text-xs text-slate-600">
                        Add part's sale price to ticket total?
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" variant="primary" className="text-xs !py-1 !px-2" disabled={!canManageParts || !selectedPartId || partQuantity <= 0}>
                        Confirm Add Part
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowAddPartForm(false)} className="text-xs !py-1 !px-2">
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {activeTab === 'checklist' && (
                 <div className="space-y-3">
                    {currentChecklist ? (
                        <>
                            <h4 className="text-sm font-semibold text-slate-700">{currentChecklist.title}</h4>
                            {currentChecklist.description && <p className="text-xs text-slate-500 mb-2">{currentChecklist.description}</p>}
                            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                            {currentChecklist.items.map(item => (
                                <div key={item.id} className="flex items-center p-2 bg-white border border-slate-200 rounded shadow-sm">
                                <input
                                    type="checkbox"
                                    id={`checklist-${ticket.id}-${item.id}`}
                                    checked={ticket.checklistProgress?.[item.id] || false}
                                    onChange={(e) => handleChecklistItemToggle(item.id, e.target.checked)}
                                    className="h-4 w-4 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)]"
                                    disabled={isUserInactive}
                                />
                                <label htmlFor={`checklist-${ticket.id}-${item.id}`} className="ml-2 text-xs text-slate-700">
                                    {item.text}
                                </label>
                                </div>
                            ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-xs text-slate-500 italic py-2">No specific checklist assigned or found for this ticket's category.</p>
                    )}
                </div>
            )}

            {activeTab === 'ai_tools' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">AI Suggested Parts/Tools:</h4>
                  <Button
                    onClick={handleSuggestParts}
                    disabled={aiPartsState === 'loading' || !canUseAITools}
                    variant="ghost"
                    className="text-xs py-1 px-2 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 flex items-center mb-2"
                    title={!canUseAITools ? "AI tools disabled" : ""}
                  >
                    <SparklesIcon className="mr-1 w-3.5 h-3.5"/> {aiPartsState === 'loading' ? "Suggesting..." : "Suggest Parts/Tools"}
                  </Button>
                  {aiPartsState === 'loading' && <div className="flex items-center text-xs text-slate-500"><LoadingIcon /> <span className="ml-1">Loading suggestions...</span></div>}
                  {partsSuggestionError && <div className="text-xs text-red-500"><ErrorIcon /> {partsSuggestionError}</div>}
                  {suggestedPartsList.length > 0 && aiPartsState === 'success' && (
                    <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                      {suggestedPartsList.map((part, index) => (
                        <div key={index} className="text-xs p-1.5 bg-white border border-slate-200 rounded shadow-sm">
                          <span className="font-medium text-slate-700">{part.name}</span>
                          {part.type && <span className="text-purple-600 text-[10px] ml-1">({part.type})</span>}
                          {part.reason && <p className="text-slate-500 text-[10px] italic_">{part.reason}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t pt-3">
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">AI Customer-Friendly Summary:</h4>
                  <Button
                    onClick={handleGenerateCustomerSummary}
                    disabled={aiCustomerSummaryState === 'loading' || !canUseAITools}
                    variant="ghost"
                     className="text-xs py-1 px-2 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 flex items-center mb-2"
                     title={!canUseAITools ? "AI tools disabled" : ""}
                  >
                    <SparklesIcon className="mr-1 w-3.5 h-3.5"/> {aiCustomerSummaryState === 'loading' ? 'Generating...' : 'Generate Customer Summary'}
                  </Button>
                  {aiCustomerSummaryState === 'loading' && <div className="flex items-center text-xs text-slate-500"><LoadingIcon /> <span className="ml-1">Generating summary...</span></div>}
                  {aiCustomerSummaryError && <div className="text-xs text-red-500"><ErrorIcon /> {aiCustomerSummaryError}</div>}
                  {aiCustomerSummaryText && aiCustomerSummaryState === 'success' && (
                    <div className="p-2 bg-white border border-slate-200 rounded shadow-sm text-xs text-slate-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {aiCustomerSummaryText}
                    </div>
                  )}
                  {aiCustomerSummaryText && aiCustomerSummaryState === 'success' && (
                    <div className="flex gap-2 mt-1">
                       <Button onClick={handleCopyCustomerSummary} variant="ghost" className="text-xs !py-1 !px-2">Copy</Button>
                       <Button onClick={handleLogCustomerSummary} variant="ghost" className="text-xs !py-1 !px-2">Log to Comms</Button>
                    </div>
                  )}
                </div>
                 <div className="border-t pt-3">
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">AI Knowledge Base Assistant:</h4>
                  <Button
                    onClick={handleSearchKnowledgeBase}
                    disabled={aiKnowledgeBaseState === 'loading' || !canUseAITools}
                    variant="ghost"
                    className="text-xs py-1 px-2 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 flex items-center mb-2"
                    title={!canUseAITools ? "AI tools disabled" : ""}
                  >
                    <BookOpenIcon className="mr-1 w-3.5 h-3.5 text-purple-500"/> {aiKnowledgeBaseState === 'loading' ? 'Searching...' : 'Search Knowledge Base'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-2 justify-start items-center">
         <Button
          onClick={() => onOpenUpdateStatusModal(ticket.id, ticket.status)}
          variant="primary"
          className="text-xs !px-3 !py-1.5"
          disabled={isUserInactive}
          title={isUserInactive ? "Inactive users cannot update status" : ""}
        >
          Update Status
        </Button>
        {canTakePaymentCondition && (
          <Button
            onClick={() => onOpenPaymentModal(ticket.id, ticket.amountDue, ticket.customer)}
            variant="secondary"
            className="text-xs !px-3 !py-1.5 !text-black hover:!brightness-90"
            disabled={!canProcessPayment}
            title={!canProcessPayment ? "You do not have permission" : ""}
          >
            Take Payment (${ticket.amountDue.toFixed(2)})
          </Button>
        )}
         <Button variant="ghost" onClick={handleOpenCheckInProcessModal} className="text-xs !px-3 !py-1.5 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10">
           <DocumentTextIcon /> Process Check-In
        </Button>
        <Button variant="ghost" onClick={handleOpenAppointmentModalForTicket} className="text-xs !px-3 !py-1.5 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10">
          <CalendarDaysIconTicket /> Schedule Appointment
        </Button>
        {onShowCustomerPortal && (
          <Button
            onClick={() => onShowCustomerPortal(ticket.id)}
            variant="ghost"
            className="text-xs !px-3 !py-1.5 !text-purple-600 hover:!text-purple-700 hover:!bg-purple-50"
            title="View Customer Portal Page"
          >
            <EyeIcon /> View Portal
          </Button>
        )}
         <Button variant="ghost" onClick={handlePrintDeviceLabel} className="text-xs !px-3 !py-1.5 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10 group">
            <TagIcon className="text-slate-500 group-hover:text-[var(--color-primary)] w-4 h-4 mr-1"/> Print Label
        </Button>
        {ticket.status === TicketStatus.COMPLETED && (
             <Button variant="ghost" onClick={handlePrintCompletionAgreement} className="text-xs !px-3 !py-1.5 !text-slate-600 hover:!text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10">
                <CheckBadgeIconTicket /> Print Completion
            </Button>
        )}
      </div>
    </div>
    {isCheckInProcessModalOpen && (
        <CheckInProcessModal
            isOpen={isCheckInProcessModalOpen}
            onClose={() => setIsCheckInProcessModalOpen(false)}
            ticket={ticket}
            appSettings={appSettings}
            onAddInternalTicketNote={onAddInternalTicketNote}
            onOpenPrintModal={onOpenPrintModal}
            currentUser={currentUser}
        />
    )}
    {isSolutionsModalOpen && (
      <AISuggestedSolutionsModal
        isOpen={isSolutionsModalOpen}
        onClose={() => setIsSolutionsModalOpen(false)}
        ticketTitle={`${ticket.id}: ${ticket.subject}`}
        isLoading={aiKnowledgeBaseState === 'loading'}
        errorText={aiKnowledgeBaseError}
        solutionsText={aiKnowledgeBaseSolutions}
      />
    )}
    </>
  );
};
